import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { projectsApi } from "@/services/projectsApi";
import { useSectionAuditRecorder } from "@/hooks/useAuditRecorder";
import { diffStringArrays } from "@/lib/auditDiff";
import type { AuditStage } from "@/constants/audit";
import type { Section, SectionSources } from "./SortableRow";

/**
 * The two source kinds a section can hold, and the field each one is recorded under.
 * Additions and removals are audited per source, so a trail entry names the source that
 * changed rather than carrying an opaque before/after list.
 */
const SOURCE_KINDS = [
  { ids: "documentIds", field: "document_id" },
  { ids: "extractionPointIds", field: "extraction_point_id" },
] as const;

type ProjectTemplateData = Awaited<ReturnType<typeof projectsApi.getProjectTemplate>>;

interface UseSectionListStateParams {
  projectId: string;
  activeTemplate: NonNullable<ProjectTemplateData>;
  templateQueryKey: string[];
  /**
   * Which workflow step is using this state. Required because the hook is shared by the
   * Report Writing document view and the Outline Creation section list — without it every
   * Outline Creation source change would be recorded against Report Writing.
   */
  stage: AuditStage;
}

export function useSectionListState({ projectId, activeTemplate, templateQueryKey, stage }: UseSectionListStateParams) {
  const queryClient = useQueryClient();
  const [sections, setSections] = useState<Section[]>([]);
  const [selectedSources, setSelectedSources] = useState<Record<string, SectionSources>>({});
  const [sourceModalSectionId, setSourceModalSectionId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const toggleCollapse = (id: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Sync sections from fetched template.
  // The server orders by ordinal which can interleave roots and children (e.g. a root
  // with ordinal 3 can appear between two children with ordinals 2 and 4).  We always
  // regroup so each root is immediately followed by its children — this keeps siblings
  // contiguous in the flat array, which the move / isFirst / isLast logic relies on.
  useEffect(() => {
    if (activeTemplate?.prompt_libraries) {
      const pls = activeTemplate.prompt_libraries;
      const grouped: typeof pls = [];
      const roots = pls.filter((pl) => !pl.parent_id);
      for (const root of roots) {
        grouped.push(root);
        grouped.push(...pls.filter((pl) => pl.parent_id === root.id));
      }
      setSections(grouped);
      const sources: Record<string, SectionSources> = {};
      for (const pl of pls) {
        sources[pl.id] = {
          documentIds: pl.sources?.document_ids ?? [],
          extractionPointIds: pl.sources?.extraction_point_ids ?? [],
        };
      }
      setSelectedSources(sources);
    }
  }, [activeTemplate]);

  const confirmDeleteSection = async () => {
    if (!pendingDeleteId) return;
    const id = pendingDeleteId;
    setPendingDeleteId(null);

    // Collect the section and any children (root sections carry their children)
    const idsToRemove = new Set(
      sections
        .filter((s) => s.id === id || s.parent_id === id)
        .map((s) => s.id)
    );

    setSections((prev) => prev.filter((s) => !idsToRemove.has(s.id)));
    queryClient.setQueryData<ProjectTemplateData>(templateQueryKey, (old) => {
      if (!old) return old;
      return {
        ...old,
        prompt_libraries: old.prompt_libraries.filter((pl) => !idsToRemove.has(pl.id)),
      };
    });

    await projectsApi.deleteProjectPromptLibrary(projectId, id).catch(() => {});
  };

  const recordSectionChange = useSectionAuditRecorder(stage, sections);

  const recordSourceChanges = (sectionId: string, before: SectionSources, after: SectionSources) => {
    for (const { ids, field } of SOURCE_KINDS) {
      const { added, removed } = diffStringArrays(before[ids], after[ids]);
      for (const id of added) recordSectionChange(sectionId, "add_source", null, { [field]: id });
      for (const id of removed) recordSectionChange(sectionId, "remove_source", { [field]: id }, null);
    }
  };

  const handleSourceSave = async (docIds: string[], epIds: string[]) => {
    if (!sourceModalSectionId) return;
    const sectionId = sourceModalSectionId;
    const previousSources = selectedSources[sectionId] ?? { documentIds: [], extractionPointIds: [] };
    setSelectedSources((prev) => ({
      ...prev,
      [sourceModalSectionId]: { documentIds: docIds, extractionPointIds: epIds },
    }));
    queryClient.setQueryData<ProjectTemplateData>(templateQueryKey, (old) => {
      if (!old) return old;
      return {
        ...old,
        prompt_libraries: old.prompt_libraries.map((pl) =>
          pl.id === sourceModalSectionId
            ? { ...pl, sources: { document_ids: docIds, extraction_point_ids: epIds } }
            : pl
        ),
      };
    });
    await projectsApi.updateProjectPromptLibrary(projectId, sectionId, {
      sources: { document_ids: docIds, extraction_point_ids: epIds },
    });
    recordSourceChanges(sectionId, previousSources, { documentIds: docIds, extractionPointIds: epIds });
  };

  return {
    sections,
    setSections,
    selectedSources,
    collapsedSections,
    setCollapsedSections,
    toggleCollapse,
    pendingDeleteId,
    setPendingDeleteId,
    confirmDeleteSection,
    sourceModalSectionId,
    setSourceModalSectionId,
    handleSourceSave,
  };
}
